/**
 * useDashboardData — KPI dinamici della Home (appuntamenti di oggi).
 *
 * Query A ristretta a [oggi 00:00, domani 00:00) Europe/Rome.
 * I tre KPI statici arrivano da `useKpiSnapshot`: qui non si toccano.
 *
 * ADMIN non interroga Firestore. Query A gli sarebbe concessa a Tier 2,
 * ma oggi la Home ADMIN è vuota per un permission-denied su Query B;
 * lasciar passare Query A da sola «migliorerebbe» i dinamici. Si skippa.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { getTodayRangeRome } from '@shared/utils/date';
import {
  fetchDashboardAppuntamenti,
  deriveAppuntamentiOggi,
  type AppuntamentoRecord,
} from '../../../services/dashboard';

const RUOLI_QUERY_A = new Set(['TITOLARE', 'SEGRETERIA', 'DIETOLOGA']);

export interface DashboardData {
  /** Count appuntamenti non cancellati oggi. */
  appuntamentiOggiCount: number;
  /** Appuntamenti non cancellati di oggi, ordinati per ora. */
  prossimiOggi: AppuntamentoRecord[];
}

export interface UseDashboardDataResult {
  data: DashboardData | null;
  loading: boolean;
  error: Error | null;
  /** Ri-esegue Query A. Non tocca lo snapshot. */
  refresh: () => void;
}

export function useDashboardData(): UseDashboardDataResult {
  const {
    role,
    loading: authLoading,
    isBackoffice,
    uidOperativo,
  } = useAuth();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const mountedRef = useRef(true);

  const refresh = useCallback(() => {
    setRefreshCount((c) => c + 1);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // PR-BO: senza attendere l'idratazione dei claim la Query A partirebbe
  // con isBackoffice=false, cioe' col vincolo di visibilita' sbagliato per
  // un account di backoffice.
  const canQuery =
    !authLoading && !!role && !!uidOperativo && RUOLI_QUERY_A.has(role);

  useEffect(() => {
    if (!canQuery || !role || !uidOperativo) return;

    const currentRole: string = role;
    // Scope della query, non identita' del chiamante: per un account di
    // backoffice con delega e' l'uid delegante. Per i ruoli studio-wide il
    // servizio lo ignora.
    const currentUid: string = uidOperativo;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const todayRange = getTodayRangeRome(now);
        const raw = await fetchDashboardAppuntamenti(
          currentRole,
          currentUid,
          now,
          isBackoffice,
        );
        if (cancelled) return;
        const oggiList = deriveAppuntamentiOggi(raw, todayRange);
        if (!mountedRef.current) return;
        setData({
          appuntamentiOggiCount: oggiList.length,
          prossimiOggi: oggiList,
        });
      } catch (err) {
        if (cancelled || !mountedRef.current) return;
        setData(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [role, uidOperativo, refreshCount, canQuery, isBackoffice]);

  if (!canQuery) {
    return { data: null, loading: false, error: null, refresh };
  }
  return { data, loading, error, refresh };
}
